<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { __ } from '@oat-sa-private/ui-core';
    import { getContext, untrack } from 'svelte';
    import Prompt from '../Prompt.svelte';
    import { getInteractionStateStore } from '../../itemsStateStore.js';
    import { getItemSessionStatusStore } from '../../itemsSessionStatusStore.js';
    import itemSessionStatus from '../../itemSessionStatus.js';
    import { hasClass, removeClass } from '../util/attributes.js';
    import ChoiceFeedbackBlock from '../feedback/ChoiceFeedbackBlock.svelte';
    import TabularMatch from './TabularMatch.svelte';
    import NonTabularMatch from './NonTabularMatch.svelte';
    import { shuffleChoicesTable } from '../util/shuffleChoices';
    import { getPositioning } from '../util/sharedVocabulary.js';
    import { getFlowDirection } from '../util/flowDirection.js';
    import MatchChoicesManagerFactory from './util/matchChoicesManager.js';

    const qtiClass = 'qti-matchInteraction';

    const baseType = 'directedPair';

    let {
        // keys for state store:
        itemIdentifier,
        responseIdentifier,

        // inherited aria attributes:
        role,
        ariaAttrs = {},

        // inherited item-level QTI attributes:
        language,
        id,
        classes = '',
        dir,

        // interaction-level QTI attributes:
        prompt,
        maxAssociations = 1,
        minAssociations = 0,
        choices = [[], []],
        shuffle = false,
        dataAttrs = {},

        cardinality
    } = $props();

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(itemIdentifier);
    const instructionsLang = itemContext && itemContext.getInstructionsLang();
    let flowDir = $derived(getFlowDirection(dir, language, itemContext));

    // svelte-ignore state_referenced_locally
    const interactionStateStore = getInteractionStateStore(itemIdentifier, responseIdentifier),
        itemSessionStatusStore = getItemSessionStatusStore(itemIdentifier);

    // svelte-ignore non_reactive_update
    let taoConstrainMaxAssociations,
        maxSelectionMessage,
        minSelectionMessage,
        firstColumnHeader,
        isTabular,
        isHiddenHeader,
        choicesPosition,
        boldTableHeader,
        feedbackPlaceholderMaxAssociations;

    setDerivedProps();

    function setDerivedProps() {
        if (shuffle) {
            choices = shuffleChoicesTable(choices, interactionStateStore);
        }

        // minAssociations should be less than maxAssociations
        minAssociations = maxAssociations > 0 ? Math.min(minAssociations, maxAssociations) : minAssociations;

        // Response format:
        if (!cardinality) {
            cardinality = maxAssociations === 1 ? 'single' : 'multiple';
        }

        taoConstrainMaxAssociations = hasClass(classes, 'tao-constrain-maxChoices');

        maxSelectionMessage = dataAttrs['data-max-selections-message'];
        minSelectionMessage = dataAttrs['data-min-selections-message'];
        firstColumnHeader = dataAttrs['data-first-column-header'];

        // interaction-level QTI attributes derived from classes:
        isTabular = !hasClass(classes, 'qti-match-non-tabular');
        isHiddenHeader = hasClass(classes, 'qti-header-hidden');
        choicesPosition = getPositioning(classes);
        // Tao-specific attributes derived from classes:
        boldTableHeader = hasClass(classes, 'bold-table-header');
        classes = removeClass(classes, 'bold-table-header');

        // if there's matchMax for tabular match rows/columns, need to always show some feedback,
        // otherwise interaction will jump when `immediateValidationMessage` gets shown
        const choicesMgr = new MatchChoicesManagerFactory(choices);
        if (
            isTabular &&
            (!minAssociations || minAssociations < 0) &&
            (!maxAssociations || maxAssociations < 0) &&
            cardinality !== 'single' &&
            choicesMgr.hasMatchMaxChoices()
        ) {
            feedbackPlaceholderMaxAssociations = choicesMgr.getPossibleAssociationsCount();
        }
    }

    // contains value of interaction
    let pairs = $state([]);

    let isInteractionFocused = $state(false); // becomes true when interaction is focused for the first time
    let interactionElement = $state();
    let immediateValidationWarning = $state(false);
    let immediateValidationMessage = $state(null);

    if (!interactionStateStore.get().qtiClass) {
        interactionStateStore.merge({ qtiClass });
    }

    loadResponse();
    $effect(() => {
        if ($interactionStateStore) {
            untrack(() => {
                loadResponse();
            });
        }
    });

    //update store on pairs value change; also will do initial response definition
    $effect(() => {
        if (pairs) {
            untrack(() => {
                storeResponse();
            });
        }
    });

    /**
     * Validate pairs
     * @returns {boolean} - validity
     */
    function getPairsValidity() {
        let validity = true;

        if (
            (minAssociations > 0 && pairs.length < minAssociations) ||
            (maxAssociations > 0 && pairs.length > maxAssociations)
        ) {
            validity = false;
        }

        // validate matchMin and matchMax properties of choices
        if (validity) {
            choices.some(choiceSet =>
                choiceSet.some(choice => {
                    const choiceUsageCount = pairs.filter(
                        pair => pair[0] === choice.key || pair[1] === choice.key
                    ).length;
                    if (
                        (choice.matchMin > 0 && choiceUsageCount < choice.matchMin) ||
                        (choice.matchMax > 0 && choiceUsageCount > choice.matchMax)
                    ) {
                        validity = false;
                    }
                    return !validity;
                })
            );
        }

        return validity;
    }

    /**
     * Loads response from store
     */
    function loadResponse() {
        let newResponse = interactionStateStore.getResponseValue();

        if (!newResponse) {
            newResponse = [];
        } else if (cardinality === 'single') {
            newResponse = [newResponse];
        }

        // compare new and old answer and load only if it is different
        if (
            newResponse.length !== pairs.length ||
            newResponse.find((pair, i) => !pairs[i] || pair[0] !== pairs[i][0] || pair[1] !== pairs[i][1])
        ) {
            pairs = newResponse;
        }

        immediateValidationMessage = null;
        immediateValidationWarning = false;
    }

    /**
     * Format and store value in interactionStateStores
     */
    function storeResponse() {
        interactionStateStore.setResponseValue(
            {
                cardinality,
                baseType,
                value: $state.snapshot(cardinality === 'single' ? pairs[0] || null : pairs)
            },
            getPairsValidity()
        );
    }

    /**
     * Handle input event, in tabular mode
     * (it may or may not result in response change, depending on constraints)
     * @param {Object} payload
     */
    function handleTabularInput(payload) {
        // due to checkboxes, in Safari it can also be interacted without firing focusin event
        isInteractionFocused = true;

        dispatchInteractiontraceEvent(payload);
    }

    /**
     * @param {Object?} payload
     * @param {Number?} payload.columnMatchMax
     * @param {Number?} payload.rowMatchMax
     * @param {Number?} payload.maxAssociations
     */
    function handleExceeded({ columnMatchMax, rowMatchMax } = {}) {
        if (columnMatchMax) {
            immediateValidationMessage = __('This column can have at most %d associations', columnMatchMax);
        } else if (rowMatchMax) {
            immediateValidationMessage = __('This row can have at most %d associations', rowMatchMax);
        } else {
            immediateValidationMessage = null;
        }
        immediateValidationWarning = true;
    }

    /**
     * Flag that focus jump inside interaction
     */
    function handleInteractionFocusIn() {
        if (!isInteractionFocused) {
            isInteractionFocused = true;
        }
    }

    let disabledBySession = $derived($itemSessionStatusStore === itemSessionStatus.closed);

    /**
     * Dispatch interactiontrace event
     * @param {Object} payload - change event payload
     */
    function dispatchInteractiontraceEvent(payload) {
        const { qtiChoiceIdentifier, type, position, target, pressedKey } = payload;

        const event = new CustomEvent('interactiontrace', {
            detail: {
                domEventType: type,
                target,
                qtiChoiceIdentifier,
                ...(position && { position }),
                ...(pressedKey && { pressedKey }),
                newResponse: pairs
            }
        });
        interactionElement.dispatchEvent(event);
    }
</script>

<style>
    .placeholder-feedback :global(.qti-instruction-container:not(:has(.warning))) {
        opacity: 0;
    }
</style>

<div
    class="qti-interaction qti-blockInteraction {qtiClass} {classes}"
    class:placeholder-feedback={feedbackPlaceholderMaxAssociations && !immediateValidationWarning}
    onfocusin={handleInteractionFocusIn}
    bind:this={interactionElement}
    lang={language}
    {id}
    {dir}
    {role}
    aria-disabled={disabledBySession}
    {...ariaAttrs}
    {...dataAttrs}>
    {#if prompt}
        <Prompt blockTree={prompt} />
    {/if}

    <ChoiceFeedbackBlock
        bind:immediateValidationWarning
        bind:immediateValidationMessage
        maxChoices={feedbackPlaceholderMaxAssociations || maxAssociations}
        minChoices={minAssociations}
        type="associations"
        qtiMaxChoicesMessage={maxSelectionMessage}
        qtiMinChoicesMessage={minSelectionMessage}
        {isInteractionFocused}
        {interactionElement}
        selectedNumber={pairs.length}
        lang={instructionsLang} />

    {#if isTabular}
        <TabularMatch
            {choices}
            {firstColumnHeader}
            {isHiddenHeader}
            {boldTableHeader}
            disabled={disabledBySession}
            {instructionsLang}
            {prompt}
            {maxAssociations}
            {taoConstrainMaxAssociations}
            isCardinalitySingle={cardinality === 'single'}
            bind:pairs
            oninput={handleTabularInput}
            onexceeded={handleExceeded} />
    {:else}
        <NonTabularMatch
            {choices}
            {choicesPosition}
            disabled={disabledBySession}
            {instructionsLang}
            contentDir={flowDir}
            {maxAssociations}
            {taoConstrainMaxAssociations}
            bind:pairs
            onexceeded={handleExceeded} />
    {/if}
</div>
